/**
 * Список «Зміна стану» (2026-08-29): модернізації + документи зміни стану
 * (консервація/ремонт/повернення/строк) в одному журналі; кнопка «+ Новий»
 * відкриває хаб-редактор з вибором виду операції.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { modernization } from '@/api/asset_movement';
import { assetStateChange, CHANGE_KIND_UA } from '@/api/assetStateChange';
import { assetsRegistry } from '@/api/assetsRegistry';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import ImportedBadge from '@/shared/components/ImportedBadge';
import { useDocListFilters, DocListFiltersRow } from '@/shared/components/DocListFilters';
import DocListFilters from '@/shared/components/DocListFilters';
import { useOrgNames } from '@/shared/hooks/useOrgNames';
import { useAttachmentFlags } from '@/shared/hooks/useAttachmentFlags';
import { signControl, type SignStatus } from '@/api/signControl';
import { AttachmentIcon, SignIcon } from '@/shared/components/StatusIcons';

type Row = { id: string; kind: 'm' | 's'; document_number: string; document_date: string; organization_id: string;
  op: string; asset_id: string; amount: string | null; posted: boolean; deletion_mark: boolean; external_source?: string | null };

export default function StateChangePage() {
  const navigate = useNavigate();
  const orgNames = useOrgNames();
  const f = useDocListFilters();
  const modQ = useQuery({ queryKey: ['fa-modernization', f.orgId], queryFn: () => modernization.list({ organization_id: f.orgId || undefined, limit: 500 }) });
  const scQ = useQuery({ queryKey: ['asset-state-change', f.orgId], queryFn: () => assetStateChange.list({ organization_id: f.orgId || undefined, limit: 500 }) });
  const assetsQ = useQuery({ queryKey: ['assets-registry-all', f.orgId], queryFn: () => assetsRegistry.list({ organization_id: f.orgId || undefined, limit: 500 }) });
  const aname = useMemo(() => new Map((assetsQ.data?.items ?? []).map((a) => [a.id, `${a.inventory_number} ${a.name}`])), [assetsQ.data]);
  // 2026-08-31 (рішення власника): колонки вкладень/підпису — два типи
  // документів у одному журналі, тому прапори тягнемо по-типово.
  const modRows = modQ.data?.items ?? [];
  const scRows = scQ.data?.items ?? [];
  const attM = useAttachmentFlags('doc.fixed_asset_modernization', modRows);
  const attS = useAttachmentFlags('doc.asset_state_change', scRows);
  const signQ = useQuery({
    queryKey: ['sign-multi-state-change', modRows.map((r) => r.id).join(','), scRows.map((r) => r.id).join(',')],
    queryFn: async () => {
      const out: Record<string, SignStatus> = {};
      const groups: Array<[string, string, string[]]> = [];
      for (const [dtype, rs] of [['doc.fixed_asset_modernization', modRows], ['doc.asset_state_change', scRows]] as const) {
        const byOrg = new Map<string, string[]>();
        rs.forEach((r) => byOrg.set(r.organization_id, [...(byOrg.get(r.organization_id) ?? []), r.id]));
        byOrg.forEach((ids, org) => groups.push([dtype, org, ids]));
      }
      await Promise.all(groups.map(async ([dtype, org, ids]) => {
        try {
          const res = await signControl.statuses(org, dtype, ids);
          if (res.enabled && res.controlled) Object.assign(out, res.statuses);
        } catch { /* тип не контролюється — колонки порожні */ }
      }));
      return out;
    },
    enabled: modRows.length + scRows.length > 0,
    staleTime: 30_000,
  });
  const signOf = (r: Row) => signQ.data?.[r.id] ?? null;
  const hasAtt = (r: Row) => r.kind === 'm' ? attM.has(r as never) : attS.has(r as never);
  const rows = useMemo<Row[]>(() => [
    ...(modQ.data?.items ?? []).map((m) => ({ id: m.id, kind: 'm' as const, document_number: m.document_number, document_date: m.document_date, organization_id: m.organization_id,
      op: 'модернізація', asset_id: m.fixed_asset_id, amount: m.total_capitalized, posted: m.posted, deletion_mark: m.deletion_mark, external_source: m.external_source })),
    ...(scQ.data?.items ?? []).map((s) => ({ id: s.id, kind: 's' as const, document_number: s.document_number, document_date: s.document_date, organization_id: s.organization_id,
      op: CHANGE_KIND_UA[s.change_kind] + (s.change_kind === 'USEFUL_LIFE' && s.new_useful_life_months ? ` → ${s.new_useful_life_months} міс` : ''),
      asset_id: s.fixed_asset_id ?? s.intangible_asset_id ?? '', amount: null, posted: s.posted, deletion_mark: s.deletion_mark, external_source: s.external_source })),
  ].filter((r) => f.status === '' || (f.status === 'posted') === r.posted), [modQ.data, scQ.data, f.status]);
  const cols: ColumnDef<Row>[] = [
    { key: 'badge', header: '', width: 40, cell: (r) => <ImportedBadge source={r.external_source} /> },
    { key: 'posted', header: 'Проведено', width: 90, cell: (r) => r.posted ? '✓' : '', sortValue: (r) => r.posted ? 1 : 0 },
    { key: 'document_number', header: '№', width: 130, cell: (r) => r.document_number },
    { key: 'document_date', header: 'Дата', width: 110, cell: (r) => new Date(r.document_date).toLocaleDateString('uk-UA'), sortValue: (r) => r.document_date },
    { key: 'organization_id', header: 'Організація', width: 180, cell: (r) => orgNames.get(r.organization_id) ?? '' },
    { key: 'op', header: 'Операція', width: 220, cell: (r) => r.op },
    { key: 'asset', header: 'Актив', cell: (r) => aname.get(r.asset_id) ?? '' },
    { key: 'amount', header: 'Сума', width: 120, align: 'right', cell: (r) => r.amount != null ? Number(r.amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 }) : '' },
    { key: '_att', header: 'Вкладення', width: 100, align: 'center',
      sortValue: (r) => hasAtt(r) ? 1 : 0, cell: (r) => <AttachmentIcon has={hasAtt(r)} /> },
    { key: 'sign_control', header: 'Підпис', width: 90, align: 'center',
      cell: (r) => signQ.data && signOf(r) ? <SignIcon status={signOf(r)} /> : (signQ.data && Object.keys(signQ.data).length ? <SignIcon status={signOf(r)} /> : '') },
  ];
  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Зміна стану ОЗ / НМА</h2>
        <button className="primary" style={{ marginLeft: 'auto' }} onClick={() => navigate('/assets/state-change/new')}>+ Новий документ</button>
      </div>
      <DocListFiltersRow><DocListFilters f={f} statusOptions={[{ value: '', label: 'Усі статуси' }, { value: 'posted', label: 'Проведені' }, { value: 'draft', label: 'Чернетки' }]} /></DocListFiltersRow>
      <DataTable rows={rows} columns={cols} rowKey={(r) => `${r.kind}-${r.id}`} loading={modQ.isLoading || scQ.isLoading} emptyText="Немає документів."
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined} initialSortKey="document_date" initialSortDir="desc" storageKey="assets-state-change"
        onRowDoubleClick={(r) => navigate(`/assets/state-change/${r.kind}/${r.id}`)} />
    </div>
  );
}
